import React, {createContext, useState, useContext} from "react";
import MenuConfig from "../MenuConfig";

// AsideMenuList renames this item at runtime once a school is selected. The
// config still carries the placeholder, so the same rename has to happen here
// for the config lookup below to produce a displayable name. Worth collapsing
// into MenuConfig itself so the name lives in one place.
const MENU_TITLE_OVERRIDES = {
  organization_hub: "Share Resources"
};

// Walk the configured menu for the item matching pathName and return the trail
// of items leading to it, outermost first. Null when nothing matches, so a
// caller can tell "no such page" from "a page at the top level".
function findTrailByPage(items, pathName) {
  for (const item of items || []) {
    const crumb = {
      pathname: item.page,
      title: MENU_TITLE_OVERRIDES[item.title] || item.title || ""
    };
    if (item.page === pathName) {
      return [crumb];
    }
    const nested = findTrailByPage(item.submenu, pathName);
    if (nested) {
      return [crumb, ...nested];
    }
  }
  return null;
}

/**
 * Resolve a page title and its breadcrumb trail from the menu configuration.
 *
 * getBreadcrumbsAndTitle below reads the *rendered* aside menu, which stays
 * empty until an async permissions call resolves. On a refresh or a deep link
 * the lookup therefore ran against an empty menu and returned "" — and callers
 * rendered an empty title as a hardcoded "Share Resources", so every directly
 * loaded page claimed to be that one. The configuration is available
 * synchronously and has no such race.
 */
export function getTitleFromMenuConfig(pathName) {
  const trail = getBreadcrumbsFromMenuConfig(pathName);
  return trail.length ? trail[trail.length - 1].title : "";
}

export function getBreadcrumbsFromMenuConfig(pathName) {
  if (!pathName) {
    return [];
  }
  return findTrailByPage(MenuConfig?.aside?.items, pathName) || [];
}

export function getBreadcrumbsAndTitle(menuId, pathName) {
  const result = {
    breadcrumbs: [],
    title: ""
  };
  const menu = document.getElementById(menuId);
  if (!menu) {
    return result;
  }


  const activeLinksArray = Array.from(menu.getElementsByClassName("active") || []);
  const activeLinks = activeLinksArray.filter(el => el.tagName === "A");
  if (!activeLinks) {
    return result;
  }

  activeLinks.forEach(link => {
    const titleSpans = link.getElementsByClassName("menu-text");
    if (titleSpans) {
      const titleSpan = Array.from(titleSpans).find(t => t.innerHTML);
      if (titleSpan) {
        result.breadcrumbs.push(
            {
              pathname: link.pathname,
              title: titleSpan.innerHTML
            }
        );
      }
    }
  });
  result.title = getTitle(result.breadcrumbs, pathName);
  return result;
}

export function getTitle(breadCrumbs, pathname) {
  if (!breadCrumbs || !pathname) {
    return "";
  }

  const item = breadCrumbs.find(b => b.pathname === pathname);
  if (!item) {
    return  "";
  }

  return  item.title;
}

const SubheaderContext = createContext();

export function useSubheader() {
  return useContext(SubheaderContext);
}

export const SubheaderConsumer = SubheaderContext.Consumer;

export function MetronicSubheaderProvider({ children }) {
  const [title, setTitle] = useState("");
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const value = { title, setTitle, breadcrumbs, setBreadcrumbs };
  return <SubheaderContext.Provider value={value}>{children}</SubheaderContext.Provider>;
}
